(() => {
  if (window.EXOBrandOrbit?.initialize) return;

  const TAU = Math.PI * 2;
  const clamp = (value, minimum, maximum) =>
    Math.min(maximum, Math.max(minimum, value));

  function initializeBrandOrbit(section) {
    if (!section || section.dataset.orbitReady === "true") return;

    const stage = section.querySelector("[data-orbit-stage]");
    const items = [...section.querySelectorAll("[data-orbit-item]")];
    const previous = section.querySelector("[data-orbit-previous]");
    const next = section.querySelector("[data-orbit-next]");
    const toggle = section.querySelector("[data-orbit-toggle]");
    const status = section.querySelector("[data-orbit-status]");
    if (!stage || items.length < 2) return;

    section.dataset.orbitReady = "true";
    section.classList.add("is-ready");

    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    const configuredVisible = Number.parseInt(
      section.dataset.orbitVisibleCount || "5",
      10,
    );
    const interval = Math.max(
      3000,
      Number.parseInt(section.dataset.orbitInterval || "6000", 10),
    );
    const orbitTilt =
      (Number.parseFloat(section.dataset.orbitTilt || "0") || 0) *
      (Math.PI / 180);
    const stepAngle = TAU / items.length;
    const autoplayVelocity = -stepAngle / interval;

    let rotation = Math.PI / 2;
    let targetRotation = rotation;
    let animationFrame = 0;
    let activeIndex = -1;
    let activeTransitionTimer = 0;
    let lastFrameTime = 0;
    let settling = false;
    let inertiaVelocity = 0;
    let dragging = false;
    let moved = false;
    let pointerId = null;
    let pointerStartX = 0;
    let pointerLastX = 0;
    let pointerLastTime = 0;
    let resumeAutoplayAt = 0;
    let visibleInViewport = true;
    let pausedByUser =
      section.dataset.orbitAutoplay !== "true" ||
      Boolean(prefersReducedMotion?.matches);
    let suppressClickUntil = 0;

    const visibleCount = () => {
      const responsiveLimit = window.innerWidth < 640 ? 3 : configuredVisible;
      return Math.min(items.length, Math.max(3, responsiveLimit));
    };

    const setActiveItem = (index, immediate = false) => {
      if (index === activeIndex) return;

      window.clearTimeout(activeTransitionTimer);
      const outgoing = activeIndex >= 0 ? items[activeIndex] : null;
      const incoming = items[index];

      items.forEach((item, itemIndex) => {
        if (itemIndex !== activeIndex && itemIndex !== index) {
          item.classList.remove(
            "is-active",
            "is-orbit-entering",
            "is-orbit-leaving",
          );
        }
      });

      if (outgoing && !immediate) {
        outgoing.classList.remove("is-orbit-entering");
        outgoing.classList.add("is-active", "is-orbit-leaving");
      } else if (outgoing) {
        outgoing.classList.remove(
          "is-active",
          "is-orbit-entering",
          "is-orbit-leaving",
        );
      }

      incoming.classList.remove("is-orbit-leaving");
      incoming.classList.add("is-active");
      if (!immediate) incoming.classList.add("is-orbit-entering");
      activeIndex = index;

      if (immediate) return;

      activeTransitionTimer = window.setTimeout(() => {
        items.forEach((item, itemIndex) => {
          item.classList.remove("is-orbit-entering", "is-orbit-leaving");
          if (itemIndex !== activeIndex) item.classList.remove("is-active");
        });
      }, 680);
    };

    const render = () => {
      const mobile = window.innerWidth < 640;
      const centerY = mobile ? 29 : 16;
      const horizontalRadius = mobile ? 52 : 47.2;
      const verticalRadius = mobile ? 11 : 9.5;
      stage.style.setProperty("--orbit-center-y", `${centerY}%`);

      const geometry = items.map((item, index) => {
        const angle = rotation + index * stepAngle;
        const orbitX = Math.cos(angle) * horizontalRadius;
        const orbitY = Math.sin(angle) * verticalRadius;
        const left =
          50 + orbitX * Math.cos(orbitTilt) - orbitY * Math.sin(orbitTilt);
        const top =
          centerY +
          orbitX * Math.sin(orbitTilt) +
          orbitY * Math.cos(orbitTilt);
        const depth = (Math.sin(angle) + 1) / 2;
        return { item, index, left, top, depth };
      });

      const visible = new Set(
        [...geometry]
          .sort((a, b) => b.depth - a.depth)
          .slice(0, visibleCount())
          .map(({ index }) => index),
      );
      const nearest = geometry.reduce((closest, candidate) =>
        candidate.depth > closest.depth ? candidate : closest,
      );
      const current = activeIndex >= 0 ? geometry[activeIndex] : null;

      if (
        !current ||
        (nearest.index !== activeIndex && nearest.depth > current.depth + 0.025)
      ) {
        setActiveItem(nearest.index, activeIndex < 0);
      }

      geometry.forEach(({ item, index, left, top, depth }) => {
        const isVisible = visible.has(index);
        const isActive = index === activeIndex;
        const isLeaving = item.classList.contains("is-orbit-leaving");
        const isPresented = isActive || isLeaving;
        const scale = 0.44 + depth * 0.4;
        const opacity = isVisible ? 0.12 + depth * 0.88 : 0;
        const blur = isVisible ? Math.max(0, (1 - depth) * 0.9) : 2;
        const perspectiveTilt = (1 - depth) * 8;
        const focusButton = item.querySelector("[data-orbit-focus]");
        const enterLink = item.querySelector("[data-orbit-enter]");

        item.style.setProperty("--orbit-left", `${left}%`);
        item.style.setProperty("--orbit-top", `${top}%`);
        item.style.setProperty("--orbit-scale", scale.toFixed(3));
        item.style.setProperty("--orbit-opacity", opacity.toFixed(3));
        item.style.setProperty("--orbit-blur", `${blur.toFixed(2)}px`);
        item.style.setProperty(
          "--orbit-perspective-tilt",
          `${perspectiveTilt.toFixed(2)}deg`,
        );
        item.style.setProperty(
          "--orbit-pointer",
          isVisible && !isLeaving ? "auto" : "none",
        );
        item.style.zIndex = String(Math.round(10 + depth * 70));
        item.setAttribute("aria-hidden", String(!isVisible && !isPresented));

        if (focusButton) {
          focusButton.tabIndex = isVisible && !isLeaving ? 0 : -1;
          focusButton.setAttribute("aria-pressed", String(isActive));
        }
        if (enterLink) enterLink.tabIndex = isActive ? 0 : -1;
      });

      if (status && activeIndex >= 0) {
        const active = items[activeIndex];
        status.value = `${String(activeIndex + 1).padStart(2, "0")} / ${String(
          items.length,
        ).padStart(2, "0")} — ${active.dataset.orbitName || "Brand"}`;
        status.textContent = status.value;
      }
    };

    const settleOn = (destination) => {
      targetRotation = destination;
      settling = true;
      inertiaVelocity = 0;
      resumeAutoplayAt = performance.now() + 850;
    };

    const focusItem = (index) => {
      const destination = Math.PI / 2 - index * stepAngle;
      const nearestTurn = Math.round((rotation - destination) / TAU);
      settleOn(destination + nearestTurn * TAU);
    };

    const move = (direction) => {
      const startingRotation = settling ? targetRotation : rotation;
      settleOn(startingRotation - direction * stepAngle);
    };

    const updateToggle = () => {
      if (!toggle) return;
      toggle.setAttribute("aria-pressed", String(pausedByUser));
      toggle.setAttribute(
        "aria-label",
        pausedByUser ? "Start automatic rotation" : "Pause automatic rotation",
      );
      toggle.textContent = pausedByUser ? "Start motion" : "Pause motion";
    };

    previous?.addEventListener("click", () => move(-1));
    next?.addEventListener("click", () => move(1));
    toggle?.addEventListener("click", () => {
      pausedByUser = !pausedByUser;
      inertiaVelocity = 0;
      resumeAutoplayAt = performance.now();
      updateToggle();
    });

    items.forEach((item, index) => {
      item
        .querySelector("[data-orbit-focus]")
        ?.addEventListener("click", (event) => {
          if (Date.now() < suppressClickUntil) {
            event.preventDefault();
            return;
          }
          focusItem(index);
        });
    });

    stage.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        move(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        move(1);
      } else if (event.key === "Home") {
        event.preventDefault();
        focusItem(0);
      }
    });

    stage.addEventListener(
      "wheel",
      (event) => {
        const horizontalIntent =
          Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
        if (!horizontalIntent) return;
        event.preventDefault();
        settling = false;
        const wheelDelta = event.deltaX + event.deltaY;
        inertiaVelocity = clamp(
          inertiaVelocity - wheelDelta * 0.000004,
          -0.0045,
          0.0045,
        );
        resumeAutoplayAt = performance.now() + 700;
      },
      { passive: false },
    );

    stage.addEventListener("pointerdown", (event) => {
      if (event.button !== 0 || event.target.closest("[data-orbit-enter]")) return;
      pointerId = event.pointerId;
      pointerStartX = event.clientX;
      pointerLastX = event.clientX;
      pointerLastTime = performance.now();
      dragging = true;
      moved = false;
      settling = false;
      inertiaVelocity = 0;
      stage.classList.add("is-dragging");
      stage.setPointerCapture?.(pointerId);
    });

    stage.addEventListener("pointermove", (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const now = performance.now();
      const deltaX = event.clientX - pointerLastX;
      const deltaTime = Math.max(1, now - pointerLastTime);
      const rotationDelta =
        (deltaX / Math.max(stage.clientWidth, 1)) * TAU * 1.15;

      if (Math.abs(event.clientX - pointerStartX) > 5) moved = true;
      rotation += rotationDelta;
      targetRotation = rotation;
      inertiaVelocity =
        inertiaVelocity * 0.68 + (rotationDelta / deltaTime) * 0.32;
      pointerLastX = event.clientX;
      pointerLastTime = now;
    });

    const finishDrag = (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      dragging = false;
      stage.classList.remove("is-dragging");
      stage.releasePointerCapture?.(pointerId);
      if (moved) {
        suppressClickUntil = Date.now() + 260;
        inertiaVelocity = clamp(inertiaVelocity, -0.0045, 0.0045);
        resumeAutoplayAt = performance.now() + 700;
      } else {
        inertiaVelocity = 0;
      }
      pointerId = null;
    };

    stage.addEventListener("pointerup", finishDrag);
    stage.addEventListener("pointercancel", finishDrag);

    window.addEventListener("resize", render, { passive: true });

    const observer = "IntersectionObserver" in window
      ? new IntersectionObserver(
          ([entry]) => {
            visibleInViewport = entry.isIntersecting;
          },
          { rootMargin: "20% 0px", threshold: 0 },
        )
      : null;
    observer?.observe(section);

    const animate = (timestamp) => {
      if (!section.isConnected) {
        observer?.disconnect();
        window.clearTimeout(activeTransitionTimer);
        return;
      }

      if (!lastFrameTime) lastFrameTime = timestamp;
      const deltaTime = clamp(timestamp - lastFrameTime, 0, 48);
      lastFrameTime = timestamp;

      if (!dragging && visibleInViewport) {
        if (settling) {
          const difference = targetRotation - rotation;
          const easing = 1 - Math.pow(0.002, deltaTime / 620);
          rotation += difference * easing;
          if (Math.abs(difference) < 0.0005) {
            rotation = targetRotation;
            settling = false;
          }
        } else if (Math.abs(inertiaVelocity) > 0.000008) {
          rotation += inertiaVelocity * deltaTime;
          inertiaVelocity *= Math.pow(0.018, deltaTime / 1000);
          if (Math.abs(inertiaVelocity) < 0.000008) inertiaVelocity = 0;
        } else if (
          !pausedByUser &&
          !document.hidden &&
          timestamp >= resumeAutoplayAt
        ) {
          rotation += autoplayVelocity * deltaTime;
        }
      }

      render();
      animationFrame = window.requestAnimationFrame(animate);
    };

    updateToggle();
    render();
    animationFrame = window.requestAnimationFrame(animate);
  }

  function initialize(root = document) {
    if (root.matches?.("[data-brand-orbit]")) initializeBrandOrbit(root);
    root
      .querySelectorAll?.("[data-brand-orbit]")
      .forEach((section) => initializeBrandOrbit(section));
  }

  window.EXOBrandOrbit = { initialize };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }
  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
  document.addEventListener("exo:brand-orbit:load", (event) =>
    initialize(event.detail?.root || document),
  );
})();
